import { escapeHtml } from "../chrome";

export interface ConfirmDialogOptions {
  title: string;
  body: string;
  /** "Confirm" by default. */
  confirmLabel?: string;
  /** "Cancel" by default. */
  cancelLabel?: string;
  /** Style the primary button as destructive (red). */
  danger?: boolean;
  /**
   * If set, user must type this exact string before the confirm button is
   * enabled. Used for irreversible operations like permanent delete.
   */
  typedConfirmation?: string;
}

/**
 * Promise-based modal confirm. Replaces window.confirm / window.prompt
 * with a properly-branded dialog. ESC, the X, and the overlay all
 * dismiss as "cancel".
 */
export function confirmDialog(opts: ConfirmDialogOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const {
      title,
      body,
      confirmLabel = "Confirm",
      cancelLabel = "Cancel",
      danger = false,
      typedConfirmation,
    } = opts;

    const overlay = document.createElement("div");
    overlay.className =
      "fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4 animate-[fadeIn_120ms_ease-out]";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", "dialog-title");

    const confirmBtnClass = danger ? "btn-danger" : "btn-primary";

    overlay.innerHTML = `
      <div class="card w-full max-w-md shadow-2xl shadow-black/50 animate-[scaleIn_140ms_ease-out]"
           role="document">
        <div class="flex items-start justify-between px-6 pt-5 pb-2">
          <h2 id="dialog-title" class="text-base font-semibold tracking-tight text-bone">
            ${escapeHtml(title)}
          </h2>
          <button data-action="close"
                  class="text-bone-muted hover:text-bone -mr-2 -mt-1 p-2 rounded"
                  aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <div class="px-6 pb-5 text-sm text-bone-muted whitespace-pre-line">${escapeHtml(body)}</div>
        ${
          typedConfirmation
            ? `
          <div class="px-6 pb-5">
            <label class="label" for="dialog-typed">Type <span class="font-mono text-bone">${escapeHtml(typedConfirmation)}</span> to confirm</label>
            <input id="dialog-typed" class="input font-mono" autocomplete="off" autocapitalize="characters" />
          </div>
        `
            : ""
        }
        <div class="px-6 py-4 bg-ink/40 border-t border-ink-border rounded-b-md flex justify-end gap-2">
          <button data-action="cancel" class="btn-ghost">${escapeHtml(cancelLabel)}</button>
          <button data-action="confirm" class="${confirmBtnClass}" ${typedConfirmation ? "disabled" : ""}>
            ${escapeHtml(confirmLabel)}
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    document.body.style.overflow = "hidden";

    const cleanup = (result: boolean): void => {
      document.body.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      overlay.remove();
      resolve(result);
    };

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") cleanup(false);
      if (e.key === "Enter" && !typedConfirmation) cleanup(true);
    };
    document.body.addEventListener("keydown", onKey);

    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) cleanup(false);
    });
    overlay
      .querySelector<HTMLButtonElement>("[data-action='close']")
      ?.addEventListener("click", () => cleanup(false));
    overlay
      .querySelector<HTMLButtonElement>("[data-action='cancel']")
      ?.addEventListener("click", () => cleanup(false));
    const confirmBtn = overlay.querySelector<HTMLButtonElement>("[data-action='confirm']");
    confirmBtn?.addEventListener("click", () => cleanup(true));

    if (typedConfirmation) {
      const input = overlay.querySelector<HTMLInputElement>("#dialog-typed");
      input?.addEventListener("input", () => {
        if (confirmBtn) confirmBtn.disabled = input.value !== typedConfirmation;
      });
      input?.addEventListener("keydown", (e) => {
        if (e.key === "Enter" && input.value === typedConfirmation) cleanup(true);
      });
      // Focus the type-to-confirm input so the user lands ready to type
      requestAnimationFrame(() => input?.focus());
    } else {
      requestAnimationFrame(() => confirmBtn?.focus());
    }
  });
}

export interface ChoiceOption<T extends string> {
  value: T;
  label: string;
  /** Optional secondary line under the label. */
  note?: string;
}

export interface ChoiceDialogOptions<T extends string> {
  title: string;
  body: string;
  options: ChoiceOption<T>[];
  /** Pre-selected value. Defaults to the first option. */
  initial?: T;
  confirmLabel?: string;
  cancelLabel?: string;
}

/**
 * Single-choice modal. Resolves with the chosen value, or null if dismissed.
 *
 * Same shell as confirmDialog — this exists because the expiry control needs
 * a value back, not a boolean, and a native <select> in a browser prompt is
 * exactly the un-branded UX the modal dialogs replaced.
 */
const CHOICE_BASE = "choice-option w-full text-left px-4 py-2.5 rounded border transition-colors";
const CHOICE_ON = "border-tfg bg-tfg/10 text-bone";
const CHOICE_OFF = "border-ink-border text-bone-muted hover:border-bone-muted";

function choiceClass(on: boolean): string {
  return `${CHOICE_BASE} ${on ? CHOICE_ON : CHOICE_OFF}`;
}

export function choiceDialog<T extends string>(opts: ChoiceDialogOptions<T>): Promise<T | null> {
  return new Promise((resolve) => {
    const {
      title,
      body,
      options,
      initial = options[0]?.value,
      confirmLabel = "Confirm",
      cancelLabel = "Cancel",
    } = opts;

    let selected: T | undefined = initial;

    const overlay = document.createElement("div");
    overlay.className =
      "fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4 animate-[fadeIn_120ms_ease-out]";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", "choice-title");

    overlay.innerHTML = `
      <div class="card w-full max-w-md shadow-2xl shadow-black/50 animate-[scaleIn_140ms_ease-out]" role="document">
        <div class="flex items-start justify-between px-6 pt-5 pb-2">
          <h2 id="choice-title" class="text-base font-semibold tracking-tight text-bone">
            ${escapeHtml(title)}
          </h2>
          <button data-action="close" class="text-bone-muted hover:text-bone -mr-2 -mt-1 p-2 rounded" aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <div class="px-6 pb-4 text-sm text-bone-muted whitespace-pre-line">${escapeHtml(body)}</div>
        <div class="px-6 pb-5 space-y-2" role="radiogroup" aria-labelledby="choice-title">
          ${options
            .map(
              (o) => `
            <button type="button" data-choice="${escapeHtml(o.value)}" role="radio"
                    aria-checked="${o.value === selected ? "true" : "false"}"
                    class="${choiceClass(o.value === selected)}">
              <div class="text-sm font-medium">${escapeHtml(o.label)}</div>
              ${o.note ? `<div class="text-xs text-bone-muted mt-0.5">${escapeHtml(o.note)}</div>` : ""}
            </button>
          `,
            )
            .join("")}
        </div>
        <div class="px-6 py-4 bg-ink/40 border-t border-ink-border rounded-b-md flex justify-end gap-2">
          <button data-action="cancel" class="btn-ghost">${escapeHtml(cancelLabel)}</button>
          <button data-action="confirm" class="btn-primary">${escapeHtml(confirmLabel)}</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    document.body.style.overflow = "hidden";

    const cleanup = (result: T | null): void => {
      document.body.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      overlay.remove();
      resolve(result);
    };

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") cleanup(null);
      if (e.key === "Enter") cleanup(selected ?? null);
    };
    document.body.addEventListener("keydown", onKey);

    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) cleanup(null);
    });
    overlay
      .querySelector<HTMLButtonElement>("[data-action='close']")
      ?.addEventListener("click", () => cleanup(null));
    overlay
      .querySelector<HTMLButtonElement>("[data-action='cancel']")
      ?.addEventListener("click", () => cleanup(null));
    overlay
      .querySelector<HTMLButtonElement>("[data-action='confirm']")
      ?.addEventListener("click", () => cleanup(selected ?? null));

    overlay.querySelectorAll<HTMLButtonElement>("[data-choice]").forEach((btn) => {
      btn.addEventListener("click", () => {
        selected = btn.dataset.choice as T;
        overlay.querySelectorAll<HTMLButtonElement>("[data-choice]").forEach((b) => {
          const on = b === btn;
          b.setAttribute("aria-checked", on ? "true" : "false");
          b.className = choiceClass(on);
        });
      });
    });

    requestAnimationFrame(() =>
      overlay.querySelector<HTMLButtonElement>("[data-action='confirm']")?.focus(),
    );
  });
}

export interface FormDialogOptions {
  title: string;
  body: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Called with the content area so the caller can mount a component into it. */
  mount: (container: HTMLElement) => void;
}

/**
 * Modal that hosts arbitrary DOM rather than a string.
 *
 * confirmDialog and choiceDialog both render fixed markup; the access editor
 * needs to mount the people picker, which manages its own listeners and state.
 * Resolves true on confirm, false on any dismissal.
 */
export function formDialog(opts: FormDialogOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const { title, body, confirmLabel = "Save", cancelLabel = "Cancel", mount } = opts;

    const overlay = document.createElement("div");
    overlay.className =
      "fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4 animate-[fadeIn_120ms_ease-out]";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", "form-dialog-title");

    overlay.innerHTML = `
      <div class="card w-full max-w-lg shadow-2xl shadow-black/50 animate-[scaleIn_140ms_ease-out]" role="document">
        <div class="flex items-start justify-between px-6 pt-5 pb-2">
          <h2 id="form-dialog-title" class="text-base font-semibold tracking-tight text-bone">
            ${escapeHtml(title)}
          </h2>
          <button data-action="close" class="text-bone-muted hover:text-bone -mr-2 -mt-1 p-2 rounded" aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <div class="px-6 pb-3 text-sm text-bone-muted whitespace-pre-line">${escapeHtml(body)}</div>
        <div id="form-dialog-content" class="px-6 pb-5"></div>
        <div class="px-6 py-4 bg-ink/40 border-t border-ink-border rounded-b-md flex justify-end gap-2">
          <button data-action="cancel" class="btn-ghost">${escapeHtml(cancelLabel)}</button>
          <button data-action="confirm" class="btn-primary">${escapeHtml(confirmLabel)}</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    document.body.style.overflow = "hidden";

    const cleanup = (result: boolean): void => {
      document.body.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      overlay.remove();
      resolve(result);
    };

    // No Enter-to-confirm: the picker uses Enter to add a typed address, and
    // stealing it would submit the dialog mid-entry.
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") cleanup(false);
    };
    document.body.addEventListener("keydown", onKey);

    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) cleanup(false);
    });
    for (const sel of ["[data-action='close']", "[data-action='cancel']"]) {
      overlay
        .querySelector<HTMLButtonElement>(sel)
        ?.addEventListener("click", () => cleanup(false));
    }
    overlay
      .querySelector<HTMLButtonElement>("[data-action='confirm']")
      ?.addEventListener("click", () => cleanup(true));

    mount(overlay.querySelector<HTMLElement>("#form-dialog-content")!);
  });
}
